import 'moment/locale/es-mx';
import 'moment/locale/pt-br';

import moment from 'moment';
import { createI18n } from 'vue-i18n';

import {
  FALLBACK_LANGUAGE_CODE,
  LANGUAGE_CODES,
  MOMENT_LOCALES,
  normalizeLanguageCode,
} from './constants';
import { getInitialLanguage, storeLanguage } from './storage';

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge(target, source) {
  Object.keys(source).forEach((key) => {
    if (isObject(source[key]) && isObject(target[key])) {
      deepMerge(target[key], source[key]);
      return;
    }

    target[key] = source[key];
  });

  return target;
}

/**
 * Builds the same nested message tree used by platform-public-frontend:
 * `features/login.json` becomes `features.login` and a `common.json` file is
 * merged directly into its parent scope.
 */
function buildTranslations(modules, locale) {
  const translations = {};
  const localePrefix = `./${locale}/`;

  modules
    .keys()
    .filter((path) => path.startsWith(localePrefix))
    .sort()
    .forEach((path) => {
      const relativePath = path.slice(localePrefix.length).replace(/\.json$/i, '');
      const parts = relativePath.split('/');
      const fileName = parts.at(-1);
      const isCommon = fileName === 'common';
      const loadedModule = modules(path);
      const content = loadedModule.default || loadedModule;

      if (isCommon && parts.length === 1) {
        deepMerge(translations, content);
        return;
      }

      const keyParts = isCommon ? parts.slice(0, -1) : parts;
      let current = translations;

      keyParts.slice(0, -1).forEach((key) => {
        if (!isObject(current[key])) {
          current[key] = {};
        }
        current = current[key];
      });

      const lastKey = keyParts.at(-1);
      if (isCommon) {
        if (!isObject(current[lastKey])) {
          current[lastKey] = {};
        }
        deepMerge(current[lastKey], content);
        return;
      }

      current[lastKey] = content;
    });

  return translations;
}

const localeModules = require.context('./locales', true, /\.json$/i);
const messages = Object.fromEntries(
  LANGUAGE_CODES.map((locale) => [locale, buildTranslations(localeModules, locale)]),
);
const initialLanguage = getInitialLanguage();

const i18n = createI18n({
  legacy: false,
  locale: initialLanguage,
  fallbackLocale: FALLBACK_LANGUAGE_CODE,
  messages,
  globalInjection: true,
});

function applyLanguageSideEffects(language) {
  moment.locale(MOMENT_LOCALES[language]);

  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}

export function setLanguage(language) {
  const normalizedLanguage = normalizeLanguageCode(language);

  if (!normalizedLanguage) {
    return false;
  }

  i18n.global.locale.value = normalizedLanguage;
  storeLanguage(normalizedLanguage);
  applyLanguageSideEffects(normalizedLanguage);
  return true;
}

applyLanguageSideEffects(initialLanguage);

export default i18n;
